import { alpha, type Theme } from "@mui/material/styles";
import type { CSSProperties } from "react";
import type { AppDataPrepComponentStyles } from "../types";

export function buildDataPrepComponentStyles(theme: Theme): AppDataPrepComponentStyles {
  const { palette } = theme;
  const isDark = palette.mode === "dark";

  return {
    toolbarPaper: { p: 1.25, bgcolor: "background.paper" },
    statusBar: {
      px: 2,
      py: 1,
      borderTop: 1,
      borderColor: "divider",
      bgcolor: "background.default",
    },
    statusBarErrorText: { maxWidth: 480 },
    flowCanvasOuter: {
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
    },
    flowCanvasInner: {
      height: { xs: 420, md: "min(560px, calc(100vh - 340px))" },
      minHeight: 360,
      width: "100%",
      position: "relative",
      "& .react-flow__controls": {
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        boxShadow: theme.shadows[2],
        borderRadius: 1,
        overflow: "hidden",
      },
      "& .react-flow__controls-button": {
        backgroundColor: palette.background.paper,
        borderBottom: `1px solid ${palette.divider}`,
        color: palette.text.secondary,
        fill: palette.text.secondary,
        "&:hover": {
          backgroundColor: palette.action.hover,
        },
        "&:last-child": {
          borderBottom: "none",
        },
        "& svg": {
          fill: "currentColor",
        },
      },
      "& .react-flow__minimap": {
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        borderRadius: 1,
        boxShadow: theme.shadows[2],
      },
      "& .react-flow__minimap-mask": {
        fill: isDark ? alpha(palette.background.default, 0.72) : alpha(palette.common.black, 0.08),
      },
      "& .react-flow__minimap-node": {
        fill: palette.primary.main,
        stroke: palette.divider,
      },
    },
    flowCanvasStyle: { width: "100%", height: "100%" },
    analysisPanelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1.5,
      borderBottom: 1,
      borderColor: "divider",
      flexShrink: 0,
    },
    analysisPanelBody: { flex: 1, minHeight: 0, p: 2, overflow: "auto" },
    accordionBordered: { border: 1, borderColor: "divider", "&:before": { display: "none" } },
    accordionDetailsNoPaddingTop: { pt: 0 },
    jsonEditorFrame: { border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" },
    recordErrorCaption: { wordBreak: "break-word" },
    runDetailHeader: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1,
      px: 2,
      py: 1.5,
      borderBottom: 1,
      borderColor: "divider",
      flexShrink: 0,
    },
    runDetailTabs: {
      flexShrink: 0,
      mx: 2,
      mt: 1.25,
      mb: 0.5,
      width: "fit-content",
      maxWidth: "calc(100% - 32px)",
    },
    runDetailBody: { flex: 1, minHeight: 0, overflow: "auto", p: 2 },
    drawerRoot: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 },
    drawerHeader: { px: 2, py: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 },
    drawerBody: { flex: 1, overflow: "auto", px: 2, py: 2 },
    drawerFooter: { px: 2, py: 1.5, flexShrink: 0 },
    drawerHintCaption: { mt: -1 },
    edgeConnectionPaper: {
      p: 1.5,
      borderRadius: 2,
      bgcolor: "action.hover",
      borderColor: "divider",
    },
    edgeConnectionLabel: { mb: 1 },
    edgeArrowIcon: { color: "text.secondary" },
    edgeJoinChip: {
      ml: { sm: "auto" },
      bgcolor: "background.paper",
      color: "text.primary",
      fontWeight: 600,
    },
    edgeSummaryText: { mt: 1.25 },
    edgeFormPadding: { p: 2 },
    edgeJoinOptionsPadding: { px: 2, py: 1.5 },
    edgeCheckboxMargin: { mt: 1 },
    chainPreviewDrawer: {
      position: "fixed",
      left: 0,
      right: 0,
      bottom: 0,
      maxHeight: "70vh",
      bgcolor: "background.paper",
      boxShadow: (theme) => theme.shadows[16],
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      outline: "none",
      overflow: "auto",
    },
    graphThumbnailEmpty: (height) => ({
      width: 120,
      height,
      borderRadius: 1,
      bgcolor: "action.hover",
      border: 1,
      borderColor: "divider",
    }),
    graphThumbnail: (height) => ({
      width: 120,
      height,
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      overflow: "hidden",
    }),
    outputNode: {
      minWidth: 180,
      p: 1.5,
      borderRadius: 2,
      border: "2px solid",
      borderColor: "secondary.main",
      bgcolor: "background.paper",
    },
    outputNodeChip: { mt: 1 },
    flowNodeTitleRow: { display: "flex", alignItems: "center", gap: 1, mb: 0.5 },
    flowNodeCaption: { display: "block" },
    runDetailFieldLabel: { display: "block", mb: 0.75 },
    dslQueryNode: ({ stale, error, tested }) => ({
      minWidth: 180,
      p: 1.5,
      borderRadius: 2,
      border: "2px solid",
      borderColor: stale
        ? "warning.main"
        : error
          ? "error.main"
          : tested
            ? "success.main"
            : "primary.main",
      bgcolor: "background.paper",
      boxShadow: 2,
    }),
    dslQueryNodeChip: { mt: 1 },
    dslQueryNodeChipWithMargin: { mt: 1, mr: 0.5 },
    correlationEdgeLabel: (labelX, labelY, incomplete) => ({
      position: "absolute",
      transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
      pointerEvents: "all",
      fontSize: 10,
      height: 22,
      borderColor: incomplete ? "warning.main" : undefined,
    }),
    correlationEdgeStyle: (incomplete, baseStyle): CSSProperties => ({
      ...baseStyle,
      strokeWidth: incomplete ? 2.5 : 2,
      stroke: incomplete ? "#ed6c02" : baseStyle?.stroke,
      strokeDasharray: incomplete ? "6 4" : undefined,
    }),
    drawerFooterOutlinedPrimary: {
      color: "primary.main",
      borderColor: "primary.main",
      "&:hover": { borderColor: "primary.dark", bgcolor: "action.hover" },
    },
    drawerFooterOutlinedError: {
      color: "error.main",
      borderColor: "error.main",
      "&:hover": {
        borderColor: "error.dark",
        bgcolor: "error.main",
        color: "error.contrastText",
      },
    },
    chainPreviewBackdrop: { bgcolor: "rgba(0,0,0,0.35)" },
    designActionsMenuPaper: { minWidth: 240 },
    autocompletePopperAboveModal: { zIndex: (theme) => theme.zIndex.modal + 1 },
  };
}
